/**
 * Frontend mirror of /app/backend/utils/seo_utils.py — keeps URL construction
 * consistent across the React app and the FastAPI crawler renderer.
 */

export const LANGS = ['fr', 'en', 'de', 'lb'];
export const DEFAULT_LANG = 'fr';

const CATEGORY_SLUGS_PER_LANG = {
  fr: {
    vehicules: 'vehicules', immobilier: 'immobilier', vacances: 'vacances',
    mode: 'mode', 'maison-jardin': 'maison-jardin',
    famille: 'famille', electronique: 'electronique', loisirs: 'loisirs',
    services: 'services', 'materiel-pro': 'materiel-pro', art: 'art',
    autres: 'autres',
  },
  en: {
    vehicules: 'vehicles', immobilier: 'real-estate', vacances: 'holiday-rentals',
    mode: 'fashion', 'maison-jardin': 'home-garden',
    famille: 'family', electronique: 'electronics', loisirs: 'leisure',
    services: 'services', 'materiel-pro': 'pro-equipment', art: 'art',
    autres: 'other',
  },
  de: {
    vehicules: 'fahrzeuge', immobilier: 'immobilien', vacances: 'ferienunterkunft',
    mode: 'mode', 'maison-jardin': 'haus-garten',
    famille: 'familie', electronique: 'elektronik', loisirs: 'freizeit',
    services: 'dienstleistungen', 'materiel-pro': 'berufsausruestung', art: 'kunst',
    autres: 'sonstiges',
  },
  lb: {
    vehicules: 'gefierer', immobilier: 'immobilien', vacances: 'vakanz',
    mode: 'moud', 'maison-jardin': 'haus-gaart',
    famille: 'famill', electronique: 'elektronik', loisirs: 'fraizait',
    services: 'servicer', 'materiel-pro': 'beruffsmaterial', art: 'konscht',
    autres: 'aner',
  },
};

const LISTING_NOUN = { fr: 'annonce', en: 'listing', de: 'anzeige', lb: 'annonce' };

export const SITE_URL = 'https://lebonlux.com';

export function slugify(text, maxLen = 60) {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, maxLen)
    .replace(/-$/, '');
}

export function categorySlugFor(lang, categoryId) {
  const set = CATEGORY_SLUGS_PER_LANG[lang] || CATEGORY_SLUGS_PER_LANG[DEFAULT_LANG];
  return set[categoryId] || categoryId;
}

export function listingNoun(lang) {
  return LISTING_NOUN[lang] || LISTING_NOUN[DEFAULT_LANG];
}

/**
 * Lang-prefixed path — for <link rel="canonical">, hreflang, sitemap, 
 * absolute external URLs. Includes /{lang}/ prefix for non-default languages
 * only; the default language (fr) lives at root ("/...") since /fr/ redirects
 * back to root.
 */
export function buildListingCanonicalPath(lang, listing) {
  const lng = LANGS.includes(lang) ? lang : DEFAULT_LANG;
  const cat = categorySlugFor(lng, listing.category || 'autres');
  const title = slugify(listing.title || 'annonce', 60);
  const city = slugify(listing.location || 'luxembourg', 40);
  const suffix = listing.seo_id || listing.id || '';
  const tail = [title, city, suffix].filter(Boolean).join('-');
  const prefix = lng === DEFAULT_LANG ? '' : `/${lng}`;
  return `${prefix}/${listingNoun(lng)}/${cat}/${tail}`;
}

/**
 * Basename-relative path (no /{lang}/ prefix) — for <Link to> and 
 * react-router navigate() calls. BrowserRouter basename adds the lang.
 */
export function buildListingPath(lang, listing) {
  const lng = LANGS.includes(lang) ? lang : DEFAULT_LANG;
  const cat = categorySlugFor(lng, listing.category || 'autres');
  const title = slugify(listing.title || 'annonce', 60);
  const city = slugify(listing.location || 'luxembourg', 40);
  const suffix = listing.seo_id || listing.id || '';
  const tail = [title, city, suffix].filter(Boolean).join('-');
  return `/${listingNoun(lng)}/${cat}/${tail}`;
}

export function buildCategoryCanonicalPath(lang, categoryId) {
  const lng = LANGS.includes(lang) ? lang : DEFAULT_LANG;
  const prefix = lng === DEFAULT_LANG ? '' : `/${lng}`;
  return `${prefix}/category/${categorySlugFor(lng, categoryId)}`;
}

export function buildCategoryPath(lang, categoryId) {
  const lng = LANGS.includes(lang) ? lang : DEFAULT_LANG;
  return `/category/${categorySlugFor(lng, categoryId)}`;
}

export function categoryIdFromSlug(slug) {
  for (const lang of LANGS) {
    for (const [id, s] of Object.entries(CATEGORY_SLUGS_PER_LANG[lang])) {
      if (s === slug) return id;
    }
  }
  return null;
}

export function allCategoryCitySlugs() {
  const set = new Set();
  LANGS.forEach((l) => Object.values(CATEGORY_SLUGS_PER_LANG[l]).forEach((s) => set.add(s)));
  return [...set];
}

// Basename-relative path for <Link to> on local SEO pages
export function buildLocalPagePath(lang, categoryId, citySlug) {
  const lng = LANGS.includes(lang) ? lang : DEFAULT_LANG;
  return `/${categorySlugFor(lng, categoryId)}/${citySlug}`;
}

export function buildLocalPageCanonicalPath(lang, categoryId, citySlug) {
  const lng = LANGS.includes(lang) ? lang : DEFAULT_LANG;
  const prefix = lng === DEFAULT_LANG ? '' : `/${lng}`;
  return `${prefix}/${categorySlugFor(lng, categoryId)}/${citySlug}`;
}

export function buildHomeCanonicalPath(lang) {
  const lng = LANGS.includes(lang) ? lang : DEFAULT_LANG;
  return lng === DEFAULT_LANG ? '/' : `/${lng}/`;
}

/**
 * Detect language from a URL pathname. Returns {lang, basename, restPath}.
 * - /en/foo → {lang: 'en', basename: '/en', restPath: '/foo'}
 * - /fr/foo → {lang: 'fr', basename: '', restPath: '/foo'} (fr is default, no prefix)
 * - /foo → {lang: 'fr', basename: '', restPath: '/foo'}
 */
export function detectLangFromPath(pathname) {
  const parts = (pathname || '/').split('/');
  const first = parts[1];
  if (LANGS.includes(first)) {
    const rest = '/' + parts.slice(2).join('/');
    if (first === DEFAULT_LANG) {
      // /fr/... is non-canonical; drop the prefix so basename stays empty.
      return { lang: DEFAULT_LANG, basename: '', restPath: rest, isFrPrefix: true };
    }
    return { lang: first, basename: `/${first}`, restPath: rest, isFrPrefix: false };
  }
  return { lang: DEFAULT_LANG, basename: '', restPath: pathname || '/', isFrPrefix: false };
}

export function hreflangAlternatesFor(builder, ...args) {
  const out = LANGS.map((l) => ({ lang: l, url: `${SITE_URL}${builder(l, ...args)}` }));
  out.push({ lang: 'x-default', url: `${SITE_URL}${builder(DEFAULT_LANG, ...args)}` });
  return out;
}

export function listingExcerpt(text, maxChars = 155) {
  if (!text) return '';
  const t = String(text).replace(/\s+/g, ' ').trim();
  if (t.length <= maxChars) return t;
  const cut = t.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut) + '…';
}

/**
 * Format the listing <title>: "{Title} – {Price}€ – {City} | LebonLux"
 */
export function buildListingTitle(listing) {
  const parts = [];
  if (listing.title) parts.push(listing.title.trim());
  if (listing.price != null && listing.price_type !== 'free') {
    const fmtPrice =
      listing.price_type === 'negotiable'
        ? `${listing.price.toLocaleString('fr-FR')}€ (négo.)`
        : `${Number(listing.price).toLocaleString('fr-FR')}€`;
    parts.push(fmtPrice);
  } else if (listing.price_type === 'free') {
    parts.push('Gratuit');
  }
  if (listing.location) parts.push(listing.location.trim());
  return parts.length > 0 ? `${parts.join(' – ')} | LebonLux` : 'LebonLux';
}
